import React from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import { gql, useApolloClient, useQuery } from '@apollo/client'
import { AppBar, Box, Button, Toolbar, Typography } from '@mui/material'
import RushmyFoodLogo from '../assets/img/rushmyfood-logo.png'
import AgentPortal from '../views/AgentPortal'
import AgentApplication from '../views/AgentApplication'
import { T } from '../components/PartnerSignups/onboardingDesign'
import { exitViewAs, viewAsInfo } from '../utils/viewAs'

// Whether this login is an ISO, from the profile the portal already loaded
// (views/AgentPortal.jsx): no request of its own.
const ROLE = gql`query AgentPortalRole { myAgentProfile { _id network { role } } }`

/**
 * The whole site for a sales agent or an ISO: the portal, and the page where
 * they fill in an application (the merchant sign-up form, framed). Styled
 * like the merchant sign-up form (components/PartnerSignups/onboardingDesign.js).
 */
export default function AgentLayout() {
  const client = useApolloClient()
  const { data: roleData } = useQuery(ROLE, { fetchPolicy: 'cache-only' })
  const isIso = !!(roleData && roleData.myAgentProfile && roleData.myAgentProfile.network && roleData.myAgentProfile.network.role === 'ISO')
  let user = {}
  try { user = JSON.parse(localStorage.getItem('user-enatega')) || {} } catch (e) { user = {} }
  // A super admin viewing as this agent or ISO (utils/viewAs.js).
  const viewing = viewAsInfo()

  const logout = async () => {
    if (viewing) {
      await exitViewAs(client)
      return
    }
    const keys = ['user-enatega', 'vendorId', 'restaurantId', 'restaurantName', 'restaurantImage']
    keys.forEach(k => localStorage.removeItem(k))
    try { await client.clearStore() } catch (e) { /* nothing cached */ }
    window.location.hash = '#/auth/login'
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: T.pageBg, fontFamily: T.body }}>
      {viewing && (
        <Box role="status" sx={{ bgcolor: T.text, color: '#fff', px: { xs: 2, sm: 3 }, py: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', fontSize: 14 }}>
          <Typography sx={{ flex: 1, minWidth: 0, fontSize: 14, fontFamily: T.body, color: '#fff' }}>
            You’re viewing <strong>{viewing.name || viewing.email}</strong>’s portal as a super admin. Anything you do here is done as them.
            {viewing.expiresAt ? ` This view ends at ${new Date(viewing.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.` : ''}
          </Typography>
          <Button
            onClick={() => exitViewAs(client)}
            sx={{ textTransform: 'none', fontFamily: T.body, fontWeight: 700, color: T.text, bgcolor: '#fff', borderRadius: 999, px: 2, minHeight: 34, '&:hover': { bgcolor: T.fill } }}
          >
            Back to admin
          </Button>
        </Box>
      )}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#fff', color: T.text, borderBottom: `1px solid ${T.cardBorder}` }}>
        <Toolbar sx={{ gap: 1.5, minHeight: 64 }}>
          <Box component="a" href="#/agent/home" aria-label="Agent portal home" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, textDecoration: 'none', color: 'inherit', flex: 1, minWidth: 0 }}>
            <Box component="img" src={RushmyFoodLogo} alt="" sx={{ height: 34 }} />
            <Typography sx={{ fontFamily: T.display, fontWeight: 700, fontSize: 19, letterSpacing: '-0.02em', color: T.text, whiteSpace: 'nowrap' }}>{isIso ? 'ISO portal' : 'Agent portal'}</Typography>
          </Box>
          <Typography sx={{ color: T.muted, fontSize: 14, fontFamily: T.body, display: { xs: 'none', sm: 'block' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</Typography>
          <Button
            onClick={logout}
            sx={{ textTransform: 'none', fontFamily: T.body, fontWeight: 600, color: T.text, border: `1px solid ${T.input}`, borderRadius: 999, px: 2, minHeight: 38, '&:hover': { background: T.fill, borderColor: T.inputHover } }}
          >
            {viewing ? 'Back to admin' : 'Log out'}
          </Button>
        </Toolbar>
      </AppBar>
      <Switch>
        <Route path="/agent/home" component={AgentPortal} />
        <Route path="/agent/application/:id" component={AgentApplication} />
        <Redirect to="/agent/home" />
      </Switch>
    </Box>
  )
}
